/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Dumbbell, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const ageGroupLabels = { "1-3": "Ages 1-3", "4-5": "Ages 4-5", "6-8": "Ages 6-8", "9-11": "Ages 9-11", "12-19": "Ages 12-19", adult: "Adults" };

const empty = { name: "", slug: "", ageGroup: "6-8", description: "", sessionDurationMinutes: 60, coachPlayerRatio: "1:10", schedule: "", price: "", published: false, activities: "", technology: "", outcomes: "" };
const toList = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);
const toText = (value) => (value || []).join(", ");
const centsToDollars = (cents) => (cents ? (cents / 100).toFixed(2) : "");

export function AdminProgramsPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [ageFilter, setAgeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredPrograms = useMemo(() => {
    return programs.filter((program) => {
      if (ageFilter !== "all" && program.ageGroup !== ageFilter) return false;
      if (statusFilter !== "all" && (statusFilter === "published") !== Boolean(program.published)) return false;
      return true;
    });
  }, [programs, ageFilter, statusFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredPrograms, {
    search,
    searchFields: ["name", "slug", "description"],
    pageSize: 6,
  });

  const load = () => {
    setLoading(true);
    api.get("/programs/manage/all")
      .then((response) => setPrograms(response.data.programs || []))
      .catch(() => toast.error("Unable to load programs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setModalOpen(true);
  };

  const openEdit = (program) => {
    setEditingId(program._id);
    setForm({
      name: program.name,
      slug: program.slug,
      ageGroup: program.ageGroup,
      description: program.description || "",
      sessionDurationMinutes: program.sessionDurationMinutes,
      coachPlayerRatio: program.coachPlayerRatio,
      schedule: program.schedule || "",
      price: centsToDollars(program.priceCents),
      published: program.published,
      activities: toText(program.activities),
      technology: toText(program.technology),
      outcomes: toText(program.outcomes),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(empty);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const { price, ...rest } = form;
    const payload = {
      ...rest,
      sessionDurationMinutes: Number(form.sessionDurationMinutes),
      priceCents: Math.round(Number(price) * 100),
      activities: toList(form.activities),
      technology: toList(form.technology),
      outcomes: toList(form.outcomes),
    };

    try {
      if (editingId) {
        const response = await api.patch(`/programs/manage/${editingId}`, payload);
        setPrograms(programs.map((p) => (p._id === editingId ? response.data.program : p)));
        toast.success("Program updated successfully!");
      } else {
        const response = await api.post("/programs/manage", payload);
        setPrograms([response.data.program, ...programs]);
        toast.success("Program created successfully!");
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save program");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (program) => {
    try {
      const response = await api.patch(`/programs/manage/${program._id}`, { published: !program.published });
      setPrograms(programs.map((p) => p._id === response.data.program._id ? response.data.program : p));
      toast.success(response.data.program.published ? "Program published." : "Program moved to draft.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update program");
    }
  };

  const remove = async (program) => {
    const ok = await confirm({
      title: "Delete this program?",
      message: `"${program.name}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete program",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/programs/manage/${program._id}`);
      setPrograms(programs.filter((p) => p._id !== program._id));
      toast.success("Program deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete program");
    }
  };

  return (
    <div>
      <PageHeader
        title="Program Management"
        subtitle="Manage age-based training programs"
        actions={
          <button type="button" className="ops-button ops-button--primary" onClick={openCreate}>
            <Plus size={16} /> New Program
          </button>
        }
      />

      <div className="ops-card">
        <h2 className="ops-card-title">All Programs</h2>
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search programs by name..."
          filters={[
            {
              label: "Age group",
              value: ageFilter,
              onChange: setAgeFilter,
              options: [{ value: "all", label: "All ages" }, ...Object.entries(ageGroupLabels).map(([value, label]) => ({ value, label }))],
            },
            {
              label: "Status",
              value: statusFilter,
              onChange: setStatusFilter,
              options: [{ value: "all", label: "All statuses" }, { value: "published", label: "Published" }, { value: "draft", label: "Draft" }],
            },
          ]}
          resultCount={totalItems}
          itemLabel="programs"
        />
        {loading ? (
          <p className="ops-loading">Loading programs...</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Dumbbell} title="No programs found" description={programs.length === 0 ? "Create your first program to get started." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <div className="ops-cards-grid">
              {pageItems.map((program) => {
                const tags = [...(program.activities || []), ...(program.technology || [])];
                return (
                  <div key={program._id} className="ops-entity-card">
                    <div className="ops-entity-card-header">
                      <div className="ops-entity-card-icon"><Dumbbell size={20} /></div>
                      <div className="ops-entity-card-heading">
                        <h3 className="ops-entity-card-title">{program.name}</h3>
                        <p className="ops-entity-card-subtitle">{program.schedule || "Schedule not specified"}</p>
                      </div>
                      <Badge tone={program.published ? "success" : "neutral"}>
                        {program.published ? "Published" : "Draft"}
                      </Badge>
                    </div>
                    <div className="ops-entity-card-stats">
                      <div className="ops-entity-card-stat">
                        <span className="ops-entity-card-stat-label">Ages</span>
                        <span className="ops-entity-card-stat-value">{ageGroupLabels[program.ageGroup] || program.ageGroup}</span>
                      </div>
                      <div className="ops-entity-card-stat">
                        <span className="ops-entity-card-stat-label">Duration</span>
                        <span className="ops-entity-card-stat-value">{program.sessionDurationMinutes} min</span>
                      </div>
                      <div className="ops-entity-card-stat">
                        <span className="ops-entity-card-stat-label">Price</span>
                        <span className="ops-entity-card-stat-value">${(program.priceCents / 100).toFixed(2)}</span>
                      </div>
                      <div className="ops-entity-card-stat">
                        <span className="ops-entity-card-stat-label">Coach ratio</span>
                        <span className="ops-entity-card-stat-value">{program.coachPlayerRatio}</span>
                      </div>
                    </div>
                    {program.description && (
                      <div className="ops-entity-card-body">
                        <p>{program.description}</p>
                      </div>
                    )}
                    {tags.length > 0 && (
                      <div className="ops-entity-card-tags">
                        {tags.map((tag) => <span key={tag} className="ops-entity-card-tag">{tag}</span>)}
                      </div>
                    )}
                    <div className="ops-entity-card-actions">
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(program)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => toggle(program)}>
                        {program.published ? "Unpublish" : "Publish"}
                      </button>
                      <button className="ops-button ops-button--danger ops-button--small" onClick={() => remove(program)} aria-label="Delete program">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? "Edit Program" : "New Program"}
        size="lg"
        footer={
          <>
            <button type="submit" form="program-form" className="ops-button ops-button--primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save Changes" : "Create Program"}
            </button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeModal}>
              Cancel
            </button>
          </>
        }
      >
        <form id="program-form" onSubmit={submit} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Basic information</p>
            <div className="ops-form-grid">
              <div>
                <label className="ops-form-label">Program name</label>
                <input
                  required
                  className="ops-input"
                  placeholder="e.g., Elite Striker Training"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div>
                <label className="ops-form-label">Slug (URL)</label>
                <input
                  required
                  className="ops-input"
                  placeholder="program-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                />
              </div>
            </div>

            <div className="ops-form-group">
              <label className="ops-form-label">Description</label>
              <textarea
                className="ops-textarea"
                placeholder="Program description..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Age group</label>
                <select
                  className="ops-select"
                  value={form.ageGroup}
                  onChange={(e) => setForm({ ...form, ageGroup: e.target.value })}
                >
                  <option value="1-3">Ages 1-3</option>
                  <option value="4-5">Ages 4-5</option>
                  <option value="6-8">Ages 6-8</option>
                  <option value="9-11">Ages 9-11</option>
                  <option value="12-19">Ages 12-19</option>
                  <option value="adult">Adults</option>
                </select>
              </div>

              <div>
                <label className="ops-form-label">Duration (minutes)</label>
                <input
                  type="number"
                  min="15"
                  className="ops-input"
                  placeholder="60"
                  value={form.sessionDurationMinutes}
                  onChange={(e) => setForm({ ...form, sessionDurationMinutes: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Schedule & pricing</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Schedule</label>
                <input
                  className="ops-input"
                  placeholder="e.g., Mondays & Wednesdays 5-6 PM"
                  value={form.schedule}
                  onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                />
              </div>

              <div>
                <label className="ops-form-label">Coach:player ratio</label>
                <input
                  className="ops-input"
                  placeholder="1:10"
                  value={form.coachPlayerRatio}
                  onChange={(e) => setForm({ ...form, coachPlayerRatio: e.target.value })}
                />
              </div>

              <div>
                <label className="ops-form-label">Price ($)</label>
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  className="ops-input"
                  placeholder="0.00"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Program content <span className="ops-form-label-optional">(optional)</span></p>
            <div className="ops-form-grid">
              <div>
                <label className="ops-form-label">Activities</label>
                <input
                  className="ops-input"
                  placeholder="Small-sided games, footwork drills, ..."
                  value={form.activities}
                  onChange={(e) => setForm({ ...form, activities: e.target.value })}
                />
                <span className="ops-form-hint">Comma-separated</span>
              </div>

              <div>
                <label className="ops-form-label">Technology</label>
                <input
                  className="ops-input"
                  placeholder="GPS vests, video analysis, ..."
                  value={form.technology}
                  onChange={(e) => setForm({ ...form, technology: e.target.value })}
                />
                <span className="ops-form-hint">Comma-separated</span>
              </div>

              <div>
                <label className="ops-form-label">Outcomes</label>
                <input
                  className="ops-input"
                  placeholder="Improved first touch, faster decision-making, ..."
                  value={form.outcomes}
                  onChange={(e) => setForm({ ...form, outcomes: e.target.value })}
                />
                <span className="ops-form-hint">Comma-separated</span>
              </div>
            </div>
          </div>

        </form>
      </Modal>
    </div>
  );
}
